import { useCancellablePromise } from "../hooks/useCancellablePromise/useCancellablePromise";
import { OrganisationDetails } from "./components/OrganisationDetails/OrganisationDetails";
import { GeographyAndMeteorology } from "./components/GeographyAndMeteorology";
import { EnergyCarousel } from "./components/EnergyCarousel/EnergyCarousel";
import { LiveMeterGraph } from "./components/LiveMeterGraph/LiveMeterGraph";
import FeedTotals from "@solarschools/component-lib/components/FeedTotals";
import { ProfileHeader } from "./components/ProfileHeader/ProfileHeader";
import { getXYChartKeyOptions } from "../utilities/Charts/getKeyOptions";
import { BreakdownGraphTooltip } from "./tooltips/BreakdownGraphTooltip";
import { LifetimeStatistics } from "./components/LifetimeStatistics";
import { GraphHeader } from "./components/GraphHeader/GraphHeader";
import { GridVMGraph } from "./components/GridVMGraph/GridVMGraph";
import { HeaderStats } from "./components/HeaderStats";
import LoadingDots from "../LoadingDots/LoadingDots";
import WeatherBackground from "./WeatherBackground";
import { HeaderBox } from "./components/HeaderBox";
import { format, addDays, parse } from "date-fns";
import NoDataAvailable from "../NoDataAvailable";
import { Heatmap } from "./components/Heatmap";
import { Summary } from "./components/Summary";
import Download from "../Icons/Download.icon";
import { Fade } from "@material-ui/core";
import PropTypes from "prop-types";
import Graph from "../Graph/Graph";
import { Parser } from "json2csv";
import React, {
  useCallback,
  useEffect,
  useState,
  Fragment,
  useRef,
} from "react";
import GraphSwitch from "./components/GraphSwitch";

const tabs = {
  DEFAULT: 0,
  LIVE: 1,
  GRIDVMS: 2,
};
let initialLoad = true;

// tech debt is bad
export const SchoolProfileView = ({ school, api, resourceType = "sites" }) => {
  /* CONSTANTS */
  // state
  const [weatherDatum, setWeatherDatum] = useState(false);
  const [energyDataFeed, setEnergyDataFeed] = useState(false);
  const [energyDataFeedFetching, setEnergyDataFeedFetching] = useState(true);
  const [lifetimeEnergyData, setLifetimeEnergyDataFeed] = useState(false);
  const [lifetimeEnergyFetching, setLifetimeEnergyFetching] = useState(true);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [subMeters, setSubMeters] = useState(false);
  const [gridVMs, setGridVMs] = useState(false);
  const [physicalMeters, setWattwatcherMeters] = useState(false);
  const [mode, setMode] = useState("TODAY");
  const [isKW, setIsKW] = useState(false);
  const [selectedDataIndex, setSelectedDataIndex] = useState(-1);
  const [subMeterData, setSubMeterData] = useState(false);
  const [graphTab, setGraphTab] = useState(0);

  const [selectedMeterIndex, setSelectedMeterIndex] = useState(0);
  const [selectedSubMeterIndex, setSelectedSubMeterIndex] = useState(0);
  const [selectedGridVMIndex, setSelectedGridVMIndex] = useState(0);
  const { cancellablePromise } = useCancellablePromise();

  // other
  const updatingDates = useRef(false);
  const showSubMeterData = selectedDataIndex > -1;
  const graphsContainer = useRef(null);

  /* CALLBACKS */
  const selectionCallback = useCallback((mode, startDate, endDate) => {
    updatingDates.current = true;
    setEnergyDataFeedFetching(true);
    setMode(mode);

    if (mode === "LIFETIME") {
      setStartDate(
        lifetimeEnergyData.startTime ?
          parse(lifetimeEnergyData.startTime, "yyyy-MM-dd HH:mm:ss", new Date()) : 
          parse(lifetimeEnergyData.firstDate, "yyyy-MM-dd", new Date())
      );
      setEndDate(
        lifetimeEnergyData.endTime ?
          parse(lifetimeEnergyData.endTime, "yyyy-MM-dd HH:mm:ss", new Date()) : 
          new Date()
      );
    } else {
      setStartDate(startDate);
      setEndDate(endDate);
    }
    updatingDates.current = false;
  }, [lifetimeEnergyData]);

  const {
    resourceId = 0,
    country,
    latitude,
    longitude,
    suburb,
    state,
    postcode,
    resourceName,
    siteName = null,
    organisationName = null,
    peakSolarCapacity,
    weather: { sunriseTime = null, sunsetTime = null } = {},
    sites = null,
    siblings = null,
    timezone,
    slug,
    summary,
    organisationSlug = null,
    weatherLocationId = null,
  } = school;

  const handleHeatMapClick = useCallback((date) => {
    selectionCallback(
      format(date, "dd-MM-yyyy", new Date()) ===
        format(new Date(), "dd-MM-yyyy")
        ? "TODAY"
        : "CUSTOM",
      date,
      date
    );
  }, []);

  /**
   * handles the click action on the export button
   */
  const handleExportClick = () => {
    const fields = [
      "apparentTemperature",
      "gridConsumed",
      "humidity",
      "netConsumed",
      "ozone",
      "solarConsumed",
      "solarGenerated",
      "solarReturned",
      "temperature",
      "time",
      "uvIndex",
    ];
    const opts = { fields };

    try {
      const parser = new Parser(opts);
      let csv = parser.parse(energyData);
      if (csv == null) return;

      let filename = "export.csv";

      if (!csv.match(/^data:text\/csv/i)) {
        csv = "data:text/csv;charset=utf-8," + csv;
      }
      let data = encodeURI(csv);

      let link = document.createElement("a");
      link.setAttribute("href", data);
      link.setAttribute("download", filename);
      link.click();
    } catch (err) {
      console.error(err);
    }
  };

  /* DATA FETCHING */
  const loadEnergyDataFeed = async (s = false, e = false) => {
    let start = format(new Date(), "yyyy-MM-dd");
    let end = format(addDays(startDate, 1), "yyyy-MM-dd");

    if (s && e) {
      start = format(s, "yyyy-MM-dd");
      end = format(addDays(e, 1), "yyyy-MM-dd");
    }
    // if all data
    try {
      const data = await api.call("data", "getEnergyFeed", [
        resourceType,
        resourceId,
        start,
        end,
        undefined,
        true,
      ]);
      setEnergyDataFeed(data);
    } catch (error) {
      console.error(error);
    }

    // if submeters
    if (subMeters[selectedDataIndex] && subMeters[selectedDataIndex].id) {
      try {
        const data = await api.call("data", "getEnergyFeed", [
          "virtualmeters",
          subMeters[selectedDataIndex].id,
          start,
          end,
        ]);
        setSubMeterData(data);
      } catch (error) {
        console.error(error);
      }
    }
    setEnergyDataFeedFetching(false);

    if (!initialLoad && graphsContainer && graphsContainer.current) {
      graphsContainer.current.scrollIntoView({behavior: 'smooth'});
    } else {
      initialLoad = false;
    }
  };

  const loadMeters = async () => {
    try {
      const data = await api.call("sites", "getVirtualMeters", [
        resourceId,
        undefined,
      ]);
      // grid VMs
      const gridVMs = data.filter((vm) => vm.typeId === 1);
      if (gridVMs) {
        setGridVMs(gridVMs);
      }

      // submeters
      const validSubmeterTypes = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
      const subMeters = data.filter((vm) =>
        validSubmeterTypes.find((type) => vm.typeId && vm.typeId === type)
      );
      if (subMeters) {
        setSubMeters(subMeters);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadWattwatcherMeters = async () => {
    if (!api.validToken()) {
      return;
    }
    try {
      const data = await api.call("sites", "getPhysicalMeters", [
        resourceId,
        undefined,
        [4],
      ]);
      if (data) {
        setWattwatcherMeters(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (resourceId > 0 && !updatingDates.current) {
      setEnergyDataFeedFetching(true);
      cancellablePromise(loadEnergyDataFeed(startDate, endDate));
    }
  }, [school, startDate, endDate, selectedDataIndex]);

  const { totals, data: energyData } = energyDataFeed || {};

  const {
    co2Produced: totalCo2Produced,
    co2Saved: totalCo2Saved,
    gridConsumed: totalGridConsumed,
    solarGenerated: totalSolarGenerated,
    solarFeedIn: totalSolarReturned,
    solarConsumed: totalSolarConsumed,
  } = totals || {};

  const loadLifetimeEnergyData = async () => {
    try {
      const data = await api.call("data", "getEnergyStats", [
        resourceType,
        resourceId,
      ]);
      setLifetimeEnergyDataFeed(data);
    } catch (e) {
      console.error(e);
    }
    setLifetimeEnergyFetching(false);
  };

  useEffect(() => {
    if (resourceId > 0) {
      cancellablePromise(loadLifetimeEnergyData());
      if (resourceType === "sites") {
        cancellablePromise(loadMeters());
        cancellablePromise(loadWattwatcherMeters());
      }
      setTimeout(() => {
        document.getElementById("profile-loader").style.display = "none";
      }, 500);
    }
  }, [resourceId]);
  const { totals: lifeTimeTotals = false, firstDate: lifeTimeStartDate } =
    lifetimeEnergyData || {};

  const {
    co2Produced: lifeTimeCo2Produced,
    co2Saved: lifeTimeCo2Saved,
    waterSaved: lifeTimeWaterSaved,
    gridConsumed: lifeTimeGridConsumed,
    solarGenerated: lifeTimeSolarGenerated,
  } = lifeTimeTotals || {};

  useEffect(() => {
    const { data } = energyDataFeed;
    if (data && !weatherDatum) {
      const index = data.findIndex(
        (v) => parse(v.time, "yyyy-MM-dd HH:mm:ss", new Date()) > new Date()
      );
      setWeatherDatum(data[index]);
    }
  }, [energyDataFeed]);

  let feedTotals = totals;
  let lifeFeedTotals = lifeTimeTotals;
  if (feedTotals) {
    feedTotals.solarCapacity = peakSolarCapacity;
  }
  if (lifeFeedTotals) {
    lifeFeedTotals.solarCapacity = peakSolarCapacity;
  }

  return (
    <Fragment>
      {weatherDatum ? (
        <WeatherBackground icon={weatherDatum.weatherIcon} />
      ) : null}
      <div className="container mt-5 mb-0">
        <div className="text-ss-dark card rounded-0 border-0 bg-transparent mb-5">
          <div className="card-body text-white py-0 px-4">
            <div className="row flex-row align-items-stretch justify-content-between">
              <ProfileHeader
                resourceName={resourceName}
                organisationName={organisationName}
                organisationSlug={organisationSlug}
                siteName={resourceType === "sites" ? siteName : ""}
                sites={sites || siblings}
                slug={slug}
              />
              <HeaderStats>
                {resourceType === "sites" || weatherLocationId ? (
                  <Fragment>
                    {!weatherDatum ? null : (
                      <HeaderBox title="Location & Live Weather">
                        <GeographyAndMeteorology
                          id={resourceId}
                          country={country}
                          latitude={latitude}
                          longitude={longitude}
                          suburb={suburb}
                          state={state}
                          postcode={postcode}
                          temperature={weatherDatum.temperature}
                          icon={weatherDatum.weatherIcon}
                          timezone={timezone}
                          sunsetTime={sunsetTime}
                          sunriseTime={sunriseTime}
                        />
                      </HeaderBox>
                    )}
                  </Fragment>
                ) : (
                  <Fragment>
                    {summary ? (
                      <HeaderBox title="Overview">
                        <OrganisationDetails
                          organisation={school}
                          summary={summary[0]}
                        />
                      </HeaderBox>
                    ) : null}
                  </Fragment>
                )}
                {lifetimeEnergyFetching ? (
                  // TODO: implement a module loading screen here
                  <HeaderBox title="Lifetime Stats">
                    <LoadingDots spinnerClass="bg-primary" />
                  </HeaderBox>
                ) : lifetimeEnergyData ? (
                  <HeaderBox
                    title={`Lifetime Stats ${
                      lifeTimeStartDate
                        ? format(
                            parse(lifeTimeStartDate, "yyyy-MM-dd", new Date()),
                            "MMM yyyy",
                            new Date()
                          )
                        : ""
                    }`}
                  >
                    <LifetimeStatistics
                      totalEnergyGenerated={lifeTimeSolarGenerated}
                      totalCo2Saved={lifeTimeCo2Saved}
                      totalWaterSaved={lifeTimeWaterSaved}
                      totalGridConsumption={lifeTimeGridConsumed}
                      totalEmissions={lifeTimeCo2Produced}
                    />
                  </HeaderBox>
                ) : null}
                {lifetimeEnergyFetching ? (
                  <HeaderBox title="">
                    <LoadingDots spinnerClass="bg-primary" />
                  </HeaderBox>
                ) : lifetimeEnergyData ? (
                  <HeaderBox title="Energy Facts">
                    <EnergyCarousel
                      totalEnergyGenerated={lifeTimeSolarGenerated}
                      totalCo2Saved={lifeTimeCo2Saved}
                      totalGridConsumption={lifeTimeGridConsumed}
                      totalEmissions={lifeTimeCo2Produced}
                      schoolStartDate={
                        lifeTimeStartDate &&
                        format(
                          parse(lifeTimeStartDate, "yyyy-MM-dd", new Date()),
                          "MMM yyyy",
                          new Date()
                        )
                      }
                    />
                  </HeaderBox>
                ) : null}
              </HeaderStats>
            </div>
          </div>

          <div className="px-4 px-md-5 pt-4 pt-md-5 mt-md-5 bg-white rounded-top" ref={graphsContainer}>
            <Summary
              selectionCallback={selectionCallback}
              solarConsumption={totalSolarConsumed}
              solarGenerated={totalSolarGenerated}
              gridConsumption={totalGridConsumed}
              solarReturned={totalSolarReturned}
              systemCapacity={peakSolarCapacity}
              fetching={energyDataFeedFetching}
              co2Produced={totalCo2Produced}
              isLive={graphTab === tabs.LIVE}
              co2Saved={totalCo2Saved}
              start={startDate}
              setIsKW={setIsKW}
              end={endDate}
              mode={mode}
            />
          </div>
          <div className="bg-white pt-4 pb-4 pb-sm-5 px-md-5 rounded-bottom position-relative">
            {/* vomit */}
            <GraphHeader
              setSelectedSubMeterIndex={setSelectedSubMeterIndex}
              setSelectedGridVMIndex={setSelectedGridVMIndex}
              setSelectedMeterIndex={setSelectedMeterIndex}
              selectedSubMeterIndex={selectedSubMeterIndex}
              setSelectedDataIndex={setSelectedDataIndex}
              user={typeof user !== "undefined" && user}
              selectedGridVMIndex={selectedGridVMIndex}
              selectedMeterIndex={selectedMeterIndex}
              selectedDataIndex={selectedDataIndex}
              physicalMeters={physicalMeters}
              resourceType={resourceType}
              setGraphTab={setGraphTab}
              subMeters={subMeters}
              graphTab={graphTab}
              gridVMs={gridVMs}
              tabs={tabs}
              api={api}
            />
            <Fade in={graphTab === tabs.LIVE}>
              <div>
                {graphTab === tabs.LIVE &&
                resourceType === "sites" &&
                physicalMeters &&
                api.validToken() &&
                typeof user !== "undefined" &&
                user &&
                user.featureAccess.includes("Live Meter Data") ? (
                  <LiveMeterGraph
                    meters={physicalMeters}
                    selectedMeterIndex={selectedMeterIndex}
                    api={api}
                  />
                ) : null}
              </div>
            </Fade>
            <Fade in={graphTab === tabs.DEFAULT}>
              <div>
                {graphTab === tabs.DEFAULT ? (
                  <Fragment>
                    {energyDataFeedFetching ? (
                      // TODO: implement a module loading screen here
                      <div
                        style={{ height: "608px" }}
                        className="aspect-ratio-content text-center d-flex flex-column justify-content-center align-items-center"
                      >
                        <LoadingDots spinnerClass="bg-primary" />
                      </div>
                    ) : energyDataFeed ? (
                      <Fragment>
                        <div className="row mx-0">
                          <div className="w-100 mr-4">
                            <div className="d-flex flex-row justify-content-between align-items-center">
                              <div
                                onClick={handleExportClick}
                                className="btn btn-sm"
                              >
                                <i className="fill-ss-dark">
                                  {typeof user !== "undefined" && (
                                    <Download
                                      className="fill-ss-dark pb-2"
                                      height="30px"
                                      width="30px"
                                    />
                                  )}
                                </i>
                              </div>
                              <BreakdownGraphTooltip />
                            </div>
                            <Graph
                              hideScroll
                              data={
                                !showSubMeterData
                                  ? energyData
                                  : subMeterData.data
                              }
                              totals={totals}
                              aggregation={
                                !showSubMeterData
                                  ? energyDataFeed.aggregation
                                  : subMeterData.aggregation
                              }
                              selectionCallback={selectionCallback}
                              keyOptions={getXYChartKeyOptions()}
                              dataKeys={
                                // normal vs submeter view
                                !showSubMeterData
                                  ? [
                                      "solarConsumed",
                                      "solarFeedIn",
                                      "gridConsumed",
                                      "solarGenerated",
                                      "netConsumed",
                                    ]
                                  : ["consumedEnergy"]
                              }
                              forceKw={isKW}
                            />
                          </div>
                          <div className="d-flex flex-column w-100 h-100 p-2">
                            {totals && (
                              <FeedTotals
                                data={
                                  mode === "LIFETIME"
                                    ? lifeFeedTotals
                                    : feedTotals
                                }
                                hasCapacity={resourceType === "sites"}
                              />
                            )}
                          </div>
                        </div>
                      </Fragment>
                    ) : (
                      <NoDataAvailable />
                    )}
                  </Fragment>
                ) : null}
              </div>
            </Fade>
            <Fade in={graphTab === tabs.GRIDVMS}>
              <div>
                {graphTab === tabs.GRIDVMS &&
                resourceType === "sites" &&
                gridVMs &&
                api.validToken() ? (
                  <GridVMGraph
                    selectedGridVMIndex={selectedGridVMIndex}
                    selectionCallback={selectionCallback}
                    startDate={startDate}
                    gridVMs={gridVMs}
                    endDate={endDate}
                    isKW={isKW}
                    api={api}
                  />
                ) : null}
              </div>
            </Fade>
            {lifetimeEnergyFetching && (
              // TODO: implement a module loading screen here
              <div className="aspect-ratio aspect-ratio-30by10">
                <div className="aspect-ratio-content text-center d-flex flex-column justify-content-center align-items-center">
                  <LoadingDots spinnerClass="bg-primary" />
                </div>
              </div>
            )}
            <Heatmap
              resourceType={resourceType}
              resourceId={resourceId}
              height={350}
              handleHeatmapClick={handleHeatMapClick}
              api={api}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

SchoolProfileView.propTypes = {
  school: PropTypes.object.isRequired,
  api: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./resources/assets/js/app/SchoolProfileView/SchoolProfileView.js